import { useState, useEffect } from 'react';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  addMonths, 
  subMonths, 
  addDays, 
  subDays, 
  startOfDay, 
  endOfDay, 
  getDay 
} from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import citaService from '../services/citaService';
import especialistaService from '../services/especialistaService';
import especialidadService from '../services/especialidadService';
import { 
  agruparCitasGrupales, 
  getEstadoColor, 
  extractTime, 
  extractDateSafe,
  fixDateOffset, 
  updateUrlParams, 
  getInitialStateFromUrl 
} from '../utils/calendarHelpers';

export const useCalendarState = (colorConfig) => {
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialState = getInitialStateFromUrl(searchParams);

  // Estados principales
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(initialState.currentDate);
  const [selectedDate, setSelectedDate] = useState(initialState.selectedDate);
  const [currentView, setCurrentView] = useState(initialState.currentView);
  const [events, setEvents] = useState([]);
  const [rawEvents, setRawEvents] = useState([]);
  
  // Estados para filtros
  const [especialistas, setEspecialistas] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [selectedEspecialista, setSelectedEspecialista] = useState(initialState.selectedEspecialista);
  const [selectedEspecialidad, setSelectedEspecialidad] = useState(initialState.selectedEspecialidad);
  
  // Estados para control de filtros por rol
  const [filtersLocked, setFiltersLocked] = useState(false);
  const [autoFiltersApplied, setAutoFiltersApplied] = useState(false);
  
  // Estados para horarios no laborables
  const [horariosNoLaborables, setHorariosNoLaborables] = useState(new Map());
  const [loadingHorarios, setLoadingHorarios] = useState(false);

  // Estados para diálogos
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Estados para acciones rápidas
  const [reprogramandoCita, setReprogramandoCita] = useState(false);
  const [nuevaFecha, setNuevaFecha] = useState('');
  const [nuevaHoraInicio, setNuevaHoraInicio] = useState('');
  const [nuevaHoraFin, setNuevaHoraFin] = useState('');

  const [programandoProximaSesion, setProgramandoProximaSesion] = useState(false);
  const [proximaFecha, setProximaFecha] = useState('');
  const [proximaHoraInicio, setProximaHoraInicio] = useState('');
  const [proximaHoraFin, setProximaHoraFin] = useState('');

  const [modificandoHorario, setModificandoHorario] = useState(false);
  const [nuevoPatronDia, setNuevoPatronDia] = useState('');
  const [nuevoPatronHoraInicio, setNuevoPatronHoraInicio] = useState('');
  const [nuevoPatronHoraFin, setNuevoPatronHoraFin] = useState('');
  const [citasAfectadas, setCitasAfectadas] = useState([]);

  const [imprimiendoHorario, setImprimiendoHorario] = useState(false);
  const [horarioParaImprimir, setHorarioParaImprimir] = useState([]);

  // Función para obtener rol del usuario
  const getUserRole = () => {
    if (!user) return null;
    
    let role;
    
    if (typeof user.rol === 'object' && user.rol !== null) {
      role = user.rol.nombre || user.rol.name || user.rol.tipo || null;
    } else if (typeof user.rol === 'string') {
      role = user.rol;
    } else {
      role = null;
    }
    
    return role;
  };

  // Verificar si debe aplicar auto-filtros
  const shouldApplyAutoFilters = () => {
    const userRole = getUserRole();
    return ['Especialista', 'Usuario Interno', 'Interno'].includes(userRole);
  };

  // Obtener filtros automáticos según rol
  const getAutoFilters = () => {
    if (!user) return { especialista: null, especialidad: null };
    
    const userRole = getUserRole();
    
    if (userRole === 'Especialista') {
      // Buscar el especialista por usuario_id en lugar de usar user.id directamente
      const especialista = especialistas.find(esp => esp.usuario?.id === user.id);
      const especialistaId = especialista?.id?.toString();
      
      return {
        especialista: especialistaId || null,
        especialidad: null
      };
    }
    
    if (userRole === 'Usuario Interno' || userRole === 'Interno') {
      const supervisorId = user.interno?.supervisor_id?.toString();
      const especialidadId = user.interno?.especialidad_id?.toString();
      
      return {
        especialista: supervisorId || null,
        especialidad: especialidadId || null
      };
    }
    
    return { especialista: null, especialidad: null };
  };

  // Función para re-aplicar colores a eventos existentes
  const reapplyColors = (eventsToProcess, colorConfigToUse) => {
    if (!colorConfigToUse || typeof colorConfigToUse.getEventColor !== 'function') {
      return eventsToProcess;
    }

    return eventsToProcess.map(event => {
      const newColor = colorConfigToUse.getEventColor(event);
      return {
        ...event,
        color: newColor
      };
    });
  };

  // Effect para re-aplicar colores cuando cambie selectedEspecialista o colorConfig
  useEffect(() => {
    if (rawEvents.length > 0 && colorConfig && typeof colorConfig.getEventColor === 'function') {
      const eventsWithNewColors = reapplyColors(rawEvents, colorConfig);
      const eventosAgrupados = agruparCitasGrupales(eventsWithNewColors);
      setEvents(eventosAgrupados);
    }
  }, [rawEvents, selectedEspecialista, colorConfig]);

  // Funciones helper para actualizar estado local
  const updateEventInLocalState = (eventId, updates) => {
    setEvents(prevEvents => 
      prevEvents.map(event => 
        event.id === eventId 
          ? { ...event, ...updates }
          : event
      )
    );
    
    setRawEvents(prevRawEvents => 
      prevRawEvents.map(event => 
        event.id === eventId 
          ? { ...event, ...updates }
          : event
      )
    );
  };

  const addEventToLocalState = (newEvent) => {
    const eventWithCorrectColor = colorConfig && typeof colorConfig.getEventColor === 'function'
      ? { ...newEvent, color: colorConfig.getEventColor(newEvent) }
      : newEvent;
    
    setEvents(prevEvents => [...prevEvents, eventWithCorrectColor]);
    setRawEvents(prevRawEvents => [...prevRawEvents, eventWithCorrectColor]);
  };

  const removeEventFromLocalState = (eventId) => {
    setEvents(prevEvents => prevEvents.filter(event => event.id !== eventId));
    setRawEvents(prevRawEvents => prevRawEvents.filter(event => event.id !== eventId));
  };

  // Cargar horarios no laborables
  const loadHorariosNoLaborables = async (especialistaId) => {
    if (!especialistaId) {
      return;
    }
    
    if (horariosNoLaborables.has(parseInt(especialistaId))) {
      return;
    }
    
    try {
      setLoadingHorarios(true);
      
      const response = await especialistaService.getHorariosEspecialista(especialistaId);
      
      setHorariosNoLaborables(prev => {
        const newMap = new Map(prev);
        newMap.set(parseInt(especialistaId), response.data?.horarios || {});
        return newMap;
      });
      
    } catch (error) {
      console.error('Error cargando horarios:', error);
      showNotification('Error al cargar horarios del especialista', 'warning');
    } finally {
      setLoadingHorarios(false);
    }
  };

  // Función para verificar horarios laborables
  const isHoraBloqueada = (fecha, hora, especialistaId) => {
    if (!especialistaId) {
      return { bloqueada: false, laborable: true, esAdministrativa: false, motivo: '' };
    }
    
    let horarios = horariosNoLaborables.get(parseInt(especialistaId));
    
    if (typeof horarios === 'string') {
      try {
        horarios = JSON.parse(horarios);
      } catch (error) {
        return { bloqueada: false, laborable: true, esAdministrativa: false, motivo: '' };
      }
    }
    
    if (!horarios) {
      return { bloqueada: false, laborable: true, esAdministrativa: false, motivo: '' };
    }
    
    const horariosGenerales = horarios.horarios_generales || horarios;
    
    if (!horariosGenerales || typeof horariosGenerales !== 'object') {
      return { bloqueada: false, laborable: true, esAdministrativa: false, motivo: '' };
    }
    
    const [year, month, day] = fecha.split('-').map(Number);
    const fechaLocal = new Date(year, month - 1, day);
    const diaNumero = fechaLocal.getDay();
    
    if (diaNumero === 0) {
      return { 
        bloqueada: true, 
        laborable: false,
        esAdministrativa: false,
        motivo: 'Los domingos no son días laborables'
      };
    }
    
    const nombresDias = {
      1: 'lunes',
      2: 'martes', 
      3: 'miercoles',
      4: 'jueves',
      5: 'viernes',
      6: 'sabado'
    };
    
    const nombreDia = nombresDias[diaNumero];
    
    if (!nombreDia) {
      return { bloqueada: true, laborable: false, esAdministrativa: false, motivo: 'Día no válido' };
    }
    
    const horariosDia = horariosGenerales[nombreDia];
    
    if (!horariosDia) {
      return { 
        bloqueada: true, 
        laborable: false,
        motivo: `${nombreDia.charAt(0).toUpperCase() + nombreDia.slice(1)} no configurado`
      };
    }
    
    if (typeof horariosDia === 'object' && horariosDia !== null) {
      const horaKey = hora.length > 5 ? hora.substring(0, 5) : hora;
      const tipoHora = horariosDia[horaKey];
      
      if (tipoHora === 'disponible') {
        return { 
          bloqueada: false, 
          laborable: true, 
          esAdministrativa: false, 
          motivo: '' 
        };
      } else if (tipoHora === 'administrativa') {
        return { 
          bloqueada: true, 
          laborable: false, 
          esAdministrativa: true,  
          motivo: 'Hora administrativa' 
        };
      } else if (tipoHora === 'no_disponible') {
        return { 
          bloqueada: true, 
          laborable: false, 
          esAdministrativa: false, 
          motivo: 'Horario no disponible' 
        };
      } else {
        return { 
          bloqueada: true, 
          laborable: false, 
          esAdministrativa: false, 
          motivo: 'Hora no definida en horario' 
        };
      }
    }
    
    return { 
      bloqueada: true, 
      laborable: false,
      motivo: 'Horario no disponible'
    };
  };

  // Función para setSelectedEspecialista con validación de bloqueo
  const handleSetSelectedEspecialista = (value) => {
    if (filtersLocked) {
      console.warn('Intento de cambiar filtro bloqueado - Especialista');
      return;
    }
    setSelectedEspecialista(value);
  };

  // Función para setSelectedEspecialidad con validación de bloqueo
  const handleSetSelectedEspecialidad = (value) => {
    if (filtersLocked) {
      console.warn('Intento de cambiar filtro bloqueado - Especialidad');
      return;
    }
    setSelectedEspecialidad(value);
  };

  // Cargar datos iniciales
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [especialistasResponse, especialidadesResponse] = await Promise.all([
          especialistaService.getEspecialistas(),
          especialidadService.getEspecialidades()
        ]);
        
        setEspecialistas(especialistasResponse.data || []);
        setEspecialidades(especialidadesResponse.data || []);
      } catch (error) {
        console.error('Error fetching initial data:', error);
        showNotification('Error al cargar los datos iniciales', 'error');
      }
    };
    
    fetchInitialData();
  }, [showNotification]);

  // Aplicar filtros automáticos cuando se cargan los datos
  useEffect(() => {
    if (user && !autoFiltersApplied && shouldApplyAutoFilters() && especialistas.length > 0) {
      const autoFilters = getAutoFilters();
      
      if (autoFilters.especialista) {
        setSelectedEspecialista(autoFilters.especialista);
        setFiltersLocked(true);
        setAutoFiltersApplied(true);
        
        showNotification(
          `Filtros aplicados automáticamente para ${getUserRole()}`,
          'info'
        );
      }
    }
  }, [user, autoFiltersApplied, especialistas]);

  // Cargar horarios cuando se selecciona especialista
  useEffect(() => {
    if (selectedEspecialista) {
      loadHorariosNoLaborables(selectedEspecialista);
    }
  }, [selectedEspecialista]);

  // Actualizar URL cuando cambian los parámetros (solo si no están bloqueados)
  useEffect(() => {
    if (!filtersLocked) {
      const updates = {
        view: currentView,
        date: format(currentView === 'day' ? selectedDate : currentDate, 'yyyy-MM-dd'),
        especialista: selectedEspecialista,
        especialidad: selectedEspecialidad
      };
      
      updateUrlParams(searchParams, setSearchParams, updates);
    }
  }, [currentView, currentDate, selectedDate, selectedEspecialista, selectedEspecialidad, searchParams, setSearchParams, filtersLocked]);

  // Cargar citas
  useEffect(() => {
    const fetchCitas = async () => {
      setLoading(true);
      try {
        let fechaInicio, fechaFin;

        if (currentView === 'month') {
          fechaInicio = format(startOfMonth(currentDate), 'yyyy-MM-dd');
          fechaFin = format(endOfMonth(currentDate), 'yyyy-MM-dd');
        } else if (currentView === 'week') {
          const day = getDay(currentDate);
          const diff = day === 0 ? 6 : day - 1;
          const weekStart = subDays(currentDate, diff);
          fechaInicio = format(weekStart, 'yyyy-MM-dd');
          fechaFin = format(addDays(weekStart, 6), 'yyyy-MM-dd');
        } else {
          fechaInicio = format(startOfDay(selectedDate), 'yyyy-MM-dd');
          fechaFin = format(endOfDay(selectedDate), 'yyyy-MM-dd');
        }

        const params = {};
        if (selectedEspecialista) {
          params.especialista_id = selectedEspecialista;
        }
        if (selectedEspecialidad) {
          params.especialidad_id = selectedEspecialidad;
        }

        
        const response = await citaService.getCalendarioCitas(fechaInicio, fechaFin, params);
 if (response.data && response.data.length > 0) {
   if (response.data[0].hora_inicio) {
   }
  if (response.data[0].start) {
   }
}
        let citasData = [];
        if (response.data && Array.isArray(response.data)) {
          citasData = response.data;
        } else if (response.data && response.data.citas && Array.isArray(response.data.citas)) {
          citasData = response.data.citas;
        }
        
        const processedEvents = citasData.map(cita => {
        // ✅ PRIORIZAR hora_inicio/hora_fin locales sobre start/end UTC
          const horaInicio = cita.horaInicio || cita.hora_inicio || '00:00:00';
          const horaFin = cita.horaFin || cita.hora_fin || horaInicio;
          const horaInicioNum = parseInt(horaInicio.split(':')[0], 10);
          
          // Usar fecha sin conversiones
          const fecha = cita.fecha || extractDateSafe(cita.start);
          
          // Procesar paciente
          const pacienteData = cita.paciente || (cita.extendedProps?.pacienteId ? {
            id: cita.extendedProps.pacienteId,
            nombres: cita.extendedProps.pacienteNombre?.split(' ').slice(0, -1).join(' ') || '',
            apellidos: cita.extendedProps.pacienteNombre?.split(' ').slice(-1)[0] || '',
            whatsapp: cita.extendedProps.pacienteWhatsapp,
            telefono: cita.extendedProps.pacienteTelefono
          } : null);
          
          // Procesar especialista
          const especialistaData = cita.especialista || (cita.extendedProps?.especialistaId ? {
            id: cita.extendedProps.especialistaId,
            usuario: {
              nombre: cita.extendedProps.especialistaNombre?.split(' ')[0] || '',
              apellidos: cita.extendedProps.especialistaNombre?.split(' ').slice(1).join(' ') || ''
            }
          } : null);
          
          // Procesar especialidad
          const especialidadData = cita.especialidad || {
            id: cita.extendedProps?.especialidadId,
            nombre: cita.extendedProps?.especialidadNombre,
            color: cita.backgroundColor
          };

          return {
            id: cita.id,
            title: cita.title || (pacienteData ? `${pacienteData.nombres} ${pacienteData.apellidos}` : 'Sin paciente'),
            fecha: fecha,
            hora_inicio: horaInicio,
            hora_fin: horaFin,
            horaInicio: horaInicio,
            horaInicioNum: horaInicioNum,
            horaFin: horaFin,
            pacienteNombre: cita.pacienteNombre || (pacienteData ? `${pacienteData.nombres} ${pacienteData.apellidos}` : 'Sin paciente'),
            especialistaNombre: cita.especialistaNombre || (especialistaData?.usuario ? `${especialistaData.usuario.nombre} ${especialistaData.usuario.apellidos}` : 'Sin especialista'),
            especialidadNombre: cita.especialidadNombre || especialidadData?.nombre || '',
            estado: cita.estado || 'Programada',
            tipo: cita.tipo || 'Consulta',
            motivo: cita.motivo || '',
            notas: cita.notas || '',
            es_evento_personal: cita.es_evento_personal || false,
            
            paciente: pacienteData,
            especialista: especialistaData,
            especialidad: especialidadData,
            
            etapaNombre: cita.etapaNombre || cita.etapa?.nombre,
            numeroSesion: cita.numeroSesion || cita.numero_sesion,
            
            extendedProps: {
              ...cita.extendedProps,
              pacienteWhatsapp: cita.extendedProps?.pacienteWhatsapp || pacienteData?.whatsapp,
              pacienteTelefono: cita.extendedProps?.pacienteTelefono || pacienteData?.telefono
            }
          };
        });

        setRawEvents(processedEvents);
        
      } catch (error) {
        console.error('Error fetching citas:', error);
        showNotification('Error al cargar las citas', 'error');
        setEvents([]);
        setRawEvents([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCitas();
  }, [currentDate, selectedDate, selectedEspecialista, selectedEspecialidad, currentView]);

  return {
    // Estados principales
    loading,
    currentDate,
    setCurrentDate,
    selectedDate,
    setSelectedDate,
    currentView,
    setCurrentView,
    events,
    setEvents,
    
    // Datos de filtros
    especialistas,
    especialidades,
    selectedEspecialista,
    setSelectedEspecialista: handleSetSelectedEspecialista,
    selectedEspecialidad,
    setSelectedEspecialidad: handleSetSelectedEspecialidad,
    
    // Estados de control por rol
    filtersLocked,
    autoFiltersApplied,
    shouldApplyAutoFilters,
    getUserRole,
    
    // Horarios no laborables
    horariosNoLaborables,
    loadingHorarios,
    isHoraBloqueada,
    
    // Estados de diálogos
    eventDialogOpen,
    setEventDialogOpen,
    selectedEvent,
    setSelectedEvent,
    
    // Estados para acciones rápidas
    reprogramandoCita,
    setReprogramandoCita,
    nuevaFecha,
    setNuevaFecha,
    nuevaHoraInicio,
    setNuevaHoraInicio,
    nuevaHoraFin,
    setNuevaHoraFin,
    
    programandoProximaSesion,
    setProgramandoProximaSesion,
    proximaFecha,
    setProximaFecha,
    proximaHoraInicio,
    setProximaHoraInicio,
    proximaHoraFin,
    setProximaHoraFin,
    
    modificandoHorario,
    setModificandoHorario,
    nuevoPatronDia,
    setNuevoPatronDia,
    nuevoPatronHoraInicio,
    setNuevoPatronHoraInicio,
    nuevoPatronHoraFin,
    setNuevoPatronHoraFin,
    citasAfectadas,
    setCitasAfectadas,
    
    imprimiendoHorario,
    setImprimiendoHorario,
    horarioParaImprimir,
    setHorarioParaImprimir,
    
    // Funciones helper
    updateEventInLocalState,
    addEventToLocalState,
    removeEventFromLocalState
  };
};